import { html } from "lit";
import { setAuthFromSession } from "./auth";
import { DEVICE } from "./device";

if (!(globalThis as any).URLPattern) {
  await import("urlpattern-polyfill");
}

import { Router } from "@thepassle/app-tools/router.js";
import { lazy } from "@thepassle/app-tools/router/plugins/lazy.js";
// @ts-ignore
import { title } from "@thepassle/app-tools/router/plugins/title.js";

import { fetchJson } from "./api";
import "../pages/app-home.js";
import "../components/app-shell.js";

const baseURL: string = (import.meta as any).env.BASE_URL;

export function resolveRouterPath(unresolvedPath?: string) {
  let resolvedPath = baseURL;
  if (unresolvedPath) resolvedPath = resolvedPath + unresolvedPath;
  return resolvedPath;
}

function isLoggedIn() {
  return sessionStorage.getItem("auth.loggedIn") === "1";
}

async function ensureAuthenticated(): Promise<boolean> {
  try {
    const res = await fetchJson(`/auth/SessionInfo`, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    });

    if (res?.success && res?.data?.authenticated === true) {
      setAuthFromSession(res.data); // <-- entscheidend
      return true;
    }

    return !!res?.success;
  } catch {
    return false;
  }
}

function guard(requestedPath: string, renderIfAuthed: () => unknown) {
  if(isLoggedIn()){
    return renderIfAuthed();
  }

  sessionStorage.setItem("auth.lastRoute", requestedPath);

  queueMicrotask(() => {
    router.navigate(resolveRouterPath("login"));
  });

  return html`
    <app-shell mode="center">
      <div style="font-weight:700;">Weiterleitung zum Login …</div>
    </app-shell>
  `;
}

function reverseGuard(renderIfNotAuthed: () => unknown) {
  if (!isLoggedIn()) return renderIfNotAuthed();

  queueMicrotask(() => {
    const target = sessionStorage.getItem("auth.lastRoute") || resolveRouterPath("intern");
    sessionStorage.removeItem("auth.lastRoute");
    router.navigate(target);
  });

  return html`
    <app-shell mode="center">
      <div style="font-weight:800;">Weiterleitung…</div>
    </app-shell>
  `;
}

function handleMobileRedirect() {
  if (!DEVICE.isMobile) return;

  const path = window.location.pathname;

  // bereits mobile route → nichts tun
  if (path.includes("/mobile/")) return;

  const map: Record<string, string> = {
    [resolveRouterPath("intern")]: resolveRouterPath("mobile/intern"),
    [resolveRouterPath("nachweis")]: resolveRouterPath("mobile/nachweis"),
    [resolveRouterPath("auftraege")]: resolveRouterPath("mobile/auftraege"),
    [resolveRouterPath("tankanlagen")]: resolveRouterPath("mobile/tankanlagen"),
    [resolveRouterPath("todo")]: resolveRouterPath("mobile/todo"),
    [resolveRouterPath("faq")]: resolveRouterPath("mobile/faq"),
    [resolveRouterPath("login")]: resolveRouterPath("mobile/login")
  };

  const target = map[path];

  if (target) {
    window.history.replaceState({}, "", target);
  }
}

handleMobileRedirect();

export const router = new Router({
  routes: [
    {
      path: resolveRouterPath(),
      title: "Ilzhöfer-Technik",
      render: () => html`<app-home></app-home>`,
    },
    {
      path: resolveRouterPath("about"),
      title: "About",
      plugins: [lazy(() => import("../pages/app-about/app-about.js"))],
      render: () => html`<app-about></app-about>`,
    },
    {
      path: resolveRouterPath("intern"),
      title: "Intern",
      plugins: [lazy(() => import("../pages/app-intern.js"))],
      render: () => guard(resolveRouterPath("intern"), () => html`<app-intern></app-intern>`),
    },
    {
      path: resolveRouterPath("mobile/intern"),
      title: "Intern",
      plugins: [lazy(() => import("../pages/mobile/app-intern-mobile"))],
      render: () => guard(
        resolveRouterPath("mobile/intern"),
        () => html`<app-intern-mobile></app-intern-mobile>`
      ),
    },
    {
      path: resolveRouterPath("support"),
      title: "Support",
      plugins: [lazy(() => import("../pages/app-support.js"))],
      render: () => guard(resolveRouterPath("support"), () => html`<app-support></app-support>`),
    },
    {
      path: resolveRouterPath("tankanlagen"),
      title: "Tankanlagen",
      plugins: [lazy(() => import("../pages/tankanlagen.js"))],
      render: () => guard(resolveRouterPath("tankanlagen"), () => html`<tankanlagen-page></tankanlagen-page>`),
    },
    {
      path: resolveRouterPath("profil"),
      title: "Profil",
      plugins: [lazy(() => import("../pages/app-profil.js"))],
      render: () => guard(resolveRouterPath("profil"), () => html`<app-profil></app-profil>`),
    },
    {
      path: resolveRouterPath("benutzerverwaltung"),
      title: "Benutzerverwaltung",
      plugins: [lazy(() => import("../pages/app-benutzerverwaltung.js"))],
      render: () => guard(resolveRouterPath("benutzerverwaltung"), () => html`<app-benutzerverwaltung></app-benutzerverwaltung>`),
    },
    {
      path: resolveRouterPath("faq"),
      title: "FAQ",
      plugins: [lazy(() => import("../pages/app-faq.js"))],
      render: () => guard(resolveRouterPath("faq"), () => html`<app-faq></app-faq>`),
    },
    {
      path: resolveRouterPath("auftraege"),
      title: "Aufträge",
      plugins: [lazy(() => import("../pages/app-auftraege.js"))],
      render: () => guard(resolveRouterPath("auftraege"), () => html`<app-auftraege></app-auftraege>`),
    },
    {
      path: resolveRouterPath("nachweis"),
      title: "Montagenachweis",
      plugins: [lazy(() => import("../pages/app-nachweis.js"))],
      render: () => guard(resolveRouterPath("nachweis"), () => html`<app-nachweis></app-nachweis>`),
    },
    {
      path: resolveRouterPath("verwaltung"),
      title: "Verwaltung",
      plugins: [lazy(() => import("../pages/app-verwaltung.js"))],
      render: () => guard(resolveRouterPath("verwaltung"), () => html`<app-verwaltung></app-verwaltung>`),
    },
    {
      path: resolveRouterPath("management"),
      title: "Kiosk-Verwaltung",
      plugins: [lazy(() => import("../pages/app-management.js"))],
      render: () => guard(resolveRouterPath("management"), () => html`<app-management></app-management>`),
    },
    {
      path: resolveRouterPath("projekte"),
      title: "Projekte",
      plugins: [lazy(() => import("../pages/app-projekte.js"))],
      render: () => guard(resolveRouterPath("projekte"), () => html`<app-projekte></app-projekte>`),
    },
    {
      path: resolveRouterPath("aufgaben"),
      title: "Aufgaben",
      plugins: [lazy(() => import("../pages/app-aufgaben.js"))],
      render: () => guard(resolveRouterPath("aufgaben"), () => html`<app-aufgaben></app-aufgaben>`),
    },
    {
      path: resolveRouterPath("triage-admin"),
      title: "Triage-Admin",
      plugins: [lazy(() => import("../pages/app-triage-admin.js"))],
      render: () => guard(resolveRouterPath("triage-admin"), () => html`<app-triage-admin></app-triage-admin>`),
    },
    {
      path: resolveRouterPath("triage-durchlaeufe"),
      title: "Triage-Durchläufe",
      plugins: [lazy(() => import("../pages/app-triage-sessions.js"))],
      render: () => guard(resolveRouterPath("triage-durchlaeufe"), () => html`<app-triage-sessions></app-triage-sessions>`),
    },
    {
      path: resolveRouterPath("triage"),
      title: "Triage",
      plugins: [lazy(() => import("../pages/app-triage.js"))],
      render: () => guard(resolveRouterPath("triage"), () => html`<app-triage></app-triage>`),
    },
    {
      path: resolveRouterPath("todo"),
      title: "ToDo",
      plugins: [lazy(() => import("../pages/app-todo.js"))],
      render: () => guard(resolveRouterPath("todo"), () => html`<app-todo></app-todo>`),
    },
    {
      path: resolveRouterPath("mobile/todo"),
      title: "Todo",
      plugins: [lazy(() => import("../pages/mobile/app-todo-mobile"))],
      render: () => guard(
        resolveRouterPath("mobile/todo"),
        () => html`<app-todo-mobile></app-todo-mobile>`
      ),
    },
    {
      path: resolveRouterPath("mobile/bautagebuch"),
      title: "Bautagebuch",
      plugins: [lazy(() => import("../pages/mobile/app-bautagebuch-mobile"))],
      render: () => guard(
        resolveRouterPath("mobile/bautagebuch"),
        () => html`<app-bautagebuch-mobile></app-bautagebuch-mobile>`
      ),
    },
    {
      path: resolveRouterPath("mobile/login"),
      title: "Login",
      plugins: [lazy(() => import("../pages/mobile/app-login-mobile"))],
      render: () => guard(
        resolveRouterPath("mobile/login"),
        () => html`<app-login-mobile></app-login-mobile>`
      ),
    },
    {
      path: resolveRouterPath("mobile/faq"),
      title: "FAQ",
      plugins: [lazy(() => import("../pages/mobile/app-faq-mobile.js"))],
      render: () => guard(
        resolveRouterPath("mobile/faq"),
        () => html`<app-faq-mobile></app-faq-mobile>`
      ),
    },
    {
      path: resolveRouterPath("login"),
      title: "Login",
      plugins: [lazy(() => import("../pages/app-login.js"))],
      render: () => reverseGuard(() => html`<app-login></app-login>`),
    }
  ],
});

// @ts-ignore
window.router = router;
